/** @format */

import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import DrawerNavbar from "../drawerNavBar/DrawerNavbar";
import UserProfileHeader from "../userprofile-header/UserProfileHeader";
import Main from "../../utility/common/Main";
import { HEADER } from "../../utility/common/config-layout";
import PaymentContributionCard from "../cards/payment-contribution-card/PaymentContributionCard";
import {
	buttonColors,
	colors,
	gridSpacing,
} from "../../utility/types/constant";
import TotalCircleCard from "../cards/total-circle-card/TotalCircleCard";
import PayOutCard from "../cards/payout-card/PayOutCard";
import PayOutScheduleCard from "../cards/payout-schedule-card/PayOutScheduleCard";
import ContributionTable from "../circle-contribution-table/ContributionTable";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store/store";
import {
	fetchAllCirclesOfMember,
	getUser,
	getUserStats,
} from "../../redux/actions/userActions";
import {
	selectCurrentUser,
	selectUserStats,
} from "../../redux/selectors/userSelector";
import {
	fetchCircleByLeader,
	getAccountRequirement,
	getAccountVerificationStatus,
} from "../../redux/actions/circleAction";
import LoadingSpinner from "../loading-spinner/LoadingSpinner";
import {
	selectAccountRequirements,
	selectAccountVerificationData,
} from "../../redux/selectors/accountVerification";
import {
	Alert,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	Paper,
	Snackbar,
	Stack,
	Typography,
} from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { keyframes } from "@mui/system";
import {
	AccountVerificationModal,
	EnterStripeDetailsForm,
} from "../allComponents/AllComponents";
import CircleLaunchpad from "../circle-launchpad/CircleLaunchpad";
import PendingInvitesCta from "../circle-invites-cta/PendingInvitesCta";
import { useLocation, useNavigate } from "react-router-dom";
import HowItWorksModal from "../how-it-works/HowItWorksModal";
import { markOnboardingComplete } from "../../redux/services/userService";

interface UserProfileProps {
	children: any;
}

const SPACING = 8;

// Animation keyframes
const fadeInScale = keyframes`
	0% {
		opacity: 0;
		transform: scale(0.9) translateY(20px);
	}
	100% {
		opacity: 1;
		transform: scale(1) translateY(0);
	}
`;

const iconFloat = keyframes`
	0%, 100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-8px);
	}
`;

const UserProfile = (props: UserProfileProps) => {
	const dispatch = useDispatch<AppDispatch>();
	const userData = useSelector(selectCurrentUser);
	const [openNav, setOpenNav] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [openVerifyAccountModal, setOpenVerifyAccountModal] = useState(false);
	const [showVerificationProcessingAlert, setShowVerificationProcessingAlert] =
		useState(false);
	const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();

	// Show How It Works modal for first-time users who haven't verified yet
	useEffect(() => {
		if (
			userData?._id &&
			userData?.hasSeenOnboarding === false &&
			!userData?.isVerified
		) {
			setShowHowItWorksModal(true);
		}
	}, [userData?._id, userData?.hasSeenOnboarding, userData?.isVerified]);

	const handleHowItWorksComplete = async () => {
		if (userData?._id) {
			try {
				await markOnboardingComplete(userData._id);
				// Refresh user data to update hasSeenOnboarding in Redux
				dispatch(getUser(userData._id));
			} catch (error) {
				console.error("Failed to mark onboarding complete:", error);
			}
		}
		setShowHowItWorksModal(false);
	};

	// Check if redirected from verification processing
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		if (params.get("verification") === "processing") {
			setShowVerificationProcessingAlert(true);
			// Clean up the URL
			navigate(location.pathname, { replace: true });
		}
	}, [location, navigate]);

	// Poll for verification status if we showed the processing alert
	useEffect(() => {
		if (!showVerificationProcessingAlert || !userData?._id) return;

		// Immediately fetch verification status
		dispatch(getAccountVerificationStatus(userData._id!));

		const pollInterval = setInterval(() => {
			dispatch(getAccountVerificationStatus(userData._id!));
			dispatch(getUser(userData._id!)); // Also refresh user data to get isVerified status
		}, 5000); // Poll every 5 seconds for faster feedback

		return () => clearInterval(pollInterval);
	}, [showVerificationProcessingAlert, userData?._id, dispatch]);

	const handleOpenVerifyAccountModal = useCallback(() => {
		setOpenVerifyAccountModal(true);
	}, []);

	const handleCloseVerifyAccountModal = () => {
		setOpenVerifyAccountModal(false);
	};

	const handleCloseVerificationProcessingAlert = () => {
		setShowVerificationProcessingAlert(false);
	};

	useEffect(() => {
		if (userData?._id) {
			dispatch(getUser(userData?._id));
			dispatch(getAccountVerificationStatus(userData?._id));
			dispatch(getUserStats(userData?._id));
		}
	}, [dispatch, userData?._id]);

	const userAccountVerified = useSelector(selectAccountVerificationData);
	const userStats = useSelector(selectUserStats);

	const {
		currently_due = [],
		past_due = [],
		eventually_due = [],
		identityVerificationStatus,
	} = useSelector(selectAccountRequirements);

	// If verification becomes complete while on this page, redirect to bank setup
	useEffect(() => {
		if (userData?.isVerified && showVerificationProcessingAlert) {
			setShowVerificationProcessingAlert(false);
			// Verification just completed - redirect to bank setup
			navigate(`/account-link/onboarding/refresh/${userData._id}`, {
				replace: true,
			});
		}
	}, [
		userData?.isVerified,
		showVerificationProcessingAlert,
		navigate,
		userData?._id,
	]);

	useEffect(() => {
		if (
			userData?.isVerified &&
			userAccountVerified?._id &&
			userData?.stripeAccountId
		) {
			dispatch(getAccountRequirement(userData?._id!)).unwrap();
		}

		if (
			userData?.isVerified &&
			userAccountVerified?._id &&
			!userData?.stripeAccountId
		) {
			return navigate(`/account-link/onboarding/refresh/${userData?._id}`, {
				replace: true,
			});
		}
	}, [
		userData?._id,
		dispatch,
		navigate,
		userData?.isVerified,
		userData?.stripeAccountId,
		userAccountVerified,
	]);

	useEffect(() => {
		if (
			userData?._id &&
			userData?.unionLeaderOf !== null
			// &&
			// userData?.stripeCustomerId !== null
		) {
			dispatch(fetchCircleByLeader(userData?._id));
		}
	}, [
		dispatch,
		userData?._id,
		userData?.unionLeaderOf,
		userData?.stripeCustomerId,
		// userSubscription,
	]);

	useEffect(() => {
		const fetchData = async () => {
			if (userData?._id && userData?.stripeCustomerId !== null) {
				try {
					setIsLoading(true);
					await dispatch(fetchAllCirclesOfMember(userData?._id)).unwrap();
				} catch (error: any) {
				} finally {
					setIsLoading(false);
				}

				setIsLoading(false);
			}
		};
		fetchData();
	}, [dispatch, userData?._id, userData?.stripeCustomerId]);

	const stripeRequirementsStillDue = Array.from(
		new Set([...currently_due, ...past_due, ...eventually_due]),
	);

	const visibleRequirements = stripeRequirementsStillDue.filter(
		(r) =>
			r !== "individual.verification.document" &&
			r !== "external_account" &&
			r !== "individual.first_name" &&
			r !== "individual.last_name",
	);

	const needsStripeInfo = visibleRequirements.length > 0;

	const requirementLabels: Record<string, string> = {
		"individual.dob.day": "Day of Birth",
		"individual.dob.month": "Month of Birth",
		"individual.dob.year": "Year of Birth",
		"individual.ssn_last_4": "Last 4 of SSN",
		// "individual.verification.document": "Identity Document",
	};

	const getRequirementLabel = (key: string) =>
		requirementLabels[key] || key.replace(/_/g, " ");

	return (
		<>
			{/* Verification Processing Notification */}
			<Snackbar
				open={showVerificationProcessingAlert}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				sx={{ mt: 2 }}>
				<Alert
					severity="info"
					onClose={handleCloseVerificationProcessingAlert}
					sx={{
						width: "100%",
						maxWidth: 600,
						boxShadow: 3,
					}}>
					<Typography variant="body1" sx={{ fontWeight: 500 }}>
						🔄 Identity Verification In Progress
					</Typography>
					<Typography variant="body2" sx={{ mt: 0.5 }}>
						Stripe is verifying your identity. This usually takes a few seconds
						to a few minutes. We're checking automatically every 5 seconds.
					</Typography>
					<Button
						size="small"
						variant="outlined"
						onClick={() => {
							if (userData?._id) {
								dispatch(getAccountVerificationStatus(userData._id));
								dispatch(getUser(userData._id));
							}
						}}
						sx={{ mt: 1, mr: 1 }}>
						Check Now
					</Button>
				</Alert>
			</Snackbar>

			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />

			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				{isLoading ?
					<LoadingSpinner
						loading={isLoading}
						size={45}
						sx={{ position: "absolute" }}
					/>
					: <DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />
				}

				{userData?.isVerified &&
					userAccountVerified?._id &&
					userData?.stripeAccountId &&
					(identityVerificationStatus === "processing" ||
						identityVerificationStatus === "pending") && (
						<Dialog
							open={needsStripeInfo}
							disableEscapeKeyDown
							fullWidth
							maxWidth="xs">
							<DialogTitle sx={{ textAlign: "center" }}>
								Identity Verification In Progress
							</DialogTitle>
							<DialogContent dividers>
								<Box
									display="flex"
									flexDirection="column"
									alignItems="center"
									justifyContent="center"
									sx={{ width: "100%", py: 2 }}>
									<Alert severity="info" sx={{ mb: 2, width: "100%" }}>
										<Typography variant="body1" sx={{ fontWeight: 500 }}>
											Please wait while Stripe verifies your identity
										</Typography>
										<Typography variant="body2" sx={{ mt: 1 }}>
											This process usually takes a few minutes. Once your
											identity is verified, you'll be prompted to provide
											additional details to complete your payout setup.
										</Typography>
									</Alert>
									<Button
										variant="outlined"
										onClick={() =>
											navigate(`/user/${userData._id}`, { replace: true })
										}
										sx={{ mt: 1 }}>
										Continue to Dashboard
									</Button>
								</Box>
							</DialogContent>
						</Dialog>
					)}

				{userData?.isVerified &&
					userAccountVerified?._id &&
					userData?.stripeAccountId &&
					identityVerificationStatus !== "processing" &&
					identityVerificationStatus !== "pending" && (
						<Dialog
							open={needsStripeInfo}
							disableEscapeKeyDown
							fullWidth
							maxWidth="xs">
							<DialogTitle sx={{ textAlign: "center" }}>
								Additional Information Needed To Complete Your Payout Setup
							</DialogTitle>
							<DialogContent dividers>
								<Box
									display="flex"
									flexDirection="column"
									alignItems="center"
									justifyContent="center"
									sx={{ width: "100%" }}>
									<Box sx={{ width: "100%", alignItems: "flex-start" }}>
										<Typography
											sx={{
												mb: 1,
												textAlign: "left",
												paddingLeft: 5,
												fontWeight: "bold",
											}}>
											We still need you to provide:
										</Typography>
										<ol
											style={{
												marginTop: 0,
												marginBottom: 16,
												paddingLeft: 40,
												textAlign: "left",
												color: colors.persianRed,
											}}>
											{visibleRequirements.map((f, idx) => (
												<li key={idx}>{getRequirementLabel(f)}</li>
											))}
										</ol>
									</Box>
									<EnterStripeDetailsForm userId={userData!._id!} />
								</Box>
							</DialogContent>
						</Dialog>
					)}

				{!userData?.isVerified &&
					(!userAccountVerified?._id || !userData?.stripeAccountId) && (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
								flexGrow: 1,
								width: "100%",
								minHeight: {
									xs: `calc(100vh - ${HEADER.H_MOBILE}px)`,
									lg: `calc(100vh - ${HEADER.H_DESKTOP}px)`,
								},
								pt: {
									xs: `${HEADER.H_MOBILE + SPACING}px`,
									lg: `${HEADER.H_DESKTOP + SPACING}px`,
								},
								pb: { xs: 4, sm: 6 },
								px: 2,
							}}>
							<PendingInvitesCta
								sx={{ width: "100%", maxWidth: "500px", mb: 3 }}
							/>
							<Paper
								elevation={0}
								sx={{
									p: { xs: 4, sm: 5 },
									maxWidth: "500px",
									width: "100%",
									margin: "0 auto",
									borderRadius: "24px",
									background:
										"linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
									boxShadow:
										"0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 25px rgba(0, 0, 0, 0.08)",
									border: "1px solid rgba(255, 255, 255, 0.8)",
									animation: `${fadeInScale} 0.6s ease-out`,
									position: "relative",
									overflow: "hidden",
									"&::before": {
										content: '""',
										position: "absolute",
										top: 0,
										left: 0,
										right: 0,
										height: "4px",
										background: `linear-gradient(90deg, ${buttonColors.blueRibbon}, #00d4ff)`,
									},
									"&:hover": {
										transform: "translateY(-4px)",
										boxShadow:
											"0 25px 70px rgba(0, 0, 0, 0.12), 0 10px 30px rgba(0, 0, 0, 0.1)",
									},
									transition: "all 0.3s ease",
								}}>
								<Stack
									direction="column"
									alignItems="center"
									justifyContent="center"
									spacing={3}>
									{/* Animated Icon */}
									<Box
										sx={{
											width: 80,
											height: 80,
											borderRadius: "50%",
											background: `linear-gradient(135deg, ${buttonColors.blueRibbon}20, ${buttonColors.blueRibbon}10)`,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											animation: `${iconFloat} 3s ease-in-out infinite`,
										}}>
										<VerifiedUserOutlinedIcon
											sx={{
												fontSize: 40,
												color: buttonColors.blueRibbon,
											}}
										/>
									</Box>

									{/* Title */}
									<Typography
										variant="h5"
										sx={{
											fontFamily: "'Montserrat', sans-serif",
											fontSize: { xs: "1.25rem", sm: "1.5rem" },
											fontWeight: 700,
											textAlign: "center",
											color: "#1a1a2e",
											lineHeight: 1.3,
										}}>
										Verify Your Identity
									</Typography>

									{/* Description */}
									<Typography
										variant="body1"
										sx={{
											fontFamily: "'Montserrat', sans-serif",
											fontSize: { xs: "0.9rem", sm: "1rem" },
											fontWeight: 400,
											textAlign: "center",
											color: "#64748b",
											lineHeight: 1.6,
											maxWidth: "380px",
										}}>
										To gain full access to our platform and start participating
										in circles, please complete the verification process.
									</Typography>

									{/* CTA Button */}
									<Button
										variant="contained"
										size="large"
										onClick={handleOpenVerifyAccountModal}
										sx={{
											px: 5,
											py: 1.5,
											background: `linear-gradient(135deg, ${buttonColors.blueRibbon} 0%, #0052cc 100%)`,
											fontSize: "1rem",
											fontWeight: 600,
											fontFamily: "'Montserrat', sans-serif",
											borderRadius: "12px",
											textTransform: "none",
											boxShadow: `0 4px 15px ${buttonColors.blueRibbon}40`,
											"&:hover": {
												background: `linear-gradient(135deg, #0052cc 0%, ${buttonColors.blueRibbon} 100%)`,
												boxShadow: `0 6px 20px ${buttonColors.blueRibbon}50`,
												transform: "translateY(-2px)",
											},
											transition: "all 0.3s ease",
										}}>
										Verify Identity
									</Button>

									{/* Subtle footer text */}
									<Typography
										variant="caption"
										sx={{
											fontFamily: "'Montserrat', sans-serif",
											color: "#94a3b8",
											fontSize: "0.75rem",
											textAlign: "center",
										}}>
										Secure verification powered by Stripe
									</Typography>
								</Stack>
							</Paper>
						</Box>
					)}

				{/* How It Works Modal for first-time users */}
				<HowItWorksModal
					open={showHowItWorksModal}
					onOpenChange={setShowHowItWorksModal}
					onGetStarted={handleHowItWorksComplete}
				/>

				{openVerifyAccountModal && (
					<AccountVerificationModal
						open={openVerifyAccountModal}
						handleClose={handleCloseVerifyAccountModal}
					/>
				)}

				{!needsStripeInfo &&
					userData?.isVerified &&
					userData?.stripeAccountId &&
					userAccountVerified?._id && (
						<Main>
							<Grid container spacing={gridSpacing}>
								<Grid item xs={12}>
									<PendingInvitesCta sx={{ mb: 3 }} />
									<CircleLaunchpad />
								</Grid>

								<Grid item xs={12}>
									<Grid container spacing={gridSpacing}>
										<Grid item lg={4} md={6} sm={6} xs={12}>
											<PaymentContributionCard
												isLoading={isLoading}
												totalContributionHistory={
													userStats?.totalContributions ?? 0
												}
											/>
										</Grid>

										<Grid item lg={4} md={6} sm={6} xs={12}>
											<TotalCircleCard
												isLoading={isLoading}
												activeCircles={userStats?.activeCirclesCount ?? 0}
											/>
										</Grid>

										<Grid item lg={4} md={12} sm={12} xs={12}>
											<Grid container spacing={gridSpacing}>
												<Grid item sm={6} xs={12} md={6} lg={12}>
													<PayOutCard
														isLoading={isLoading}
														totalPayOuts={userStats?.totalPayOuts ?? 0}
													/>
												</Grid>

												<Grid item sm={6} xs={12} md={6} lg={12}>
													<PayOutScheduleCard
														isLoading={isLoading}
														expectedPayOuts={
															userStats?.totalExpectedPayOuts ?? 0
														}
													/>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>

								{isLoading ?
									<LoadingSpinner
										loading={isLoading}
										size={45}
										sx={{ position: "absolute" }}
									/>
									: <Grid item xs={12}>
										<Grid container spacing={gridSpacing}>
											<Grid item xs={12} md={12} lg={12}>
												<ContributionTable />
											</Grid>
										</Grid>
									</Grid>
								}
							</Grid>
						</Main>
					)}
			</Box>
		</>
	);
};

export default UserProfile;

UserProfile.propTypes = {
	children: PropTypes.node,
};
